import { Lightning } from '@lightningjs/sdk';
import { PageId } from 'types/pageId';

import Page from 'components/Page';
import RecommendedSection from 'components/pages/movie-details/RecommendedSection';
import { Episode, FeaturedTrailer, Show, ShowDetails } from 'types/api/media';
import ShowcaseSection, {
  SHOWCASE_SECTION_HEIGHT,
} from 'components/common/ShowcaseSection';
import { getImageTextureObj } from 'support/generalUtils';
import { navigateToPlay } from 'support/routerUtils';
import { StaticViewContexts } from 'types/analytics';
import { ListItemContext } from 'types/events';
import { ContentBookmarks } from 'types/history';

export interface MovieDetailsPageSpec extends Lightning.Component.TemplateSpec {
  pageData: {
    show: ShowDetails;
    recommended: Show[];
    listItemContext: ListItemContext;
    fromPageId: PageId;
    isMyStuffAdded: boolean;
    history: ContentBookmarks;
  };

  Content: {
    Sections: {
      ShowcaseSection: typeof ShowcaseSection;
      RecommendedSection: typeof RecommendedSection;
    };
    Logo: Lightning.textures.ImageTexture;
  };
}

const SECTION_SCROLL_Y = 213;
const RECOMMENDED_SECTION_FOCUSED_Y =
  SHOWCASE_SECTION_HEIGHT - SECTION_SCROLL_Y;

const LOGO_X_SHOWCASE = 96;
const LOGO_Y_SHOWCASE = 85;
const LOGO_W_SHOWCASE = 565;
const LOGO_H_SHOWCASE = 230;
const LOGO_Y_RECOMMENDED = LOGO_Y_SHOWCASE - SECTION_SCROLL_Y;

export default class MovieDetailsPage
  extends Page<MovieDetailsPageSpec>
  implements Lightning.Component.ImplementTemplateSpec<MovieDetailsPageSpec>
{
  protected override _pageId = PageId.MOVIE_DETAIL;
  protected override _viewContext = StaticViewContexts.MOVIE_DETAIL_PAGE;
  private _listItemContext: ListItemContext | undefined = undefined;

  private _movie: ShowDetails | null = null;
  private _featuredEpisode: Episode | null = null;
  private _featuredTrailer: FeaturedTrailer | null = null;

  private _Content = this.getByRef('Content')!;
  private _Logo = this._Content.getByRef('Logo')!;
  private _Sections = this._Content.getByRef('Sections')!;
  private _ShowcaseSection = this._Sections.getByRef('ShowcaseSection')!;
  private _RecommendedSection = this._Sections.getByRef('RecommendedSection')!;

  static override _template(): Lightning.Component.Template<MovieDetailsPageSpec> {
    return {
      ...super._template(),
      Content: {
        h: 1080,
        Sections: {
          ShowcaseSection: {
            type: ShowcaseSection,
            signals: {
              $modalToggled: '$modalToggled',
              $playContent: '$playContent',
              $playTrailer: '$playTrailer',
              $onShowcaseHover: '$onShowcaseHover',
              $addMyStuff: '$addMyStuff',
            },
          },
          RecommendedSection: {
            y: SHOWCASE_SECTION_HEIGHT,
            type: RecommendedSection,
            signals: {
              $onRecommendedHover: '$onRecommendedHover',
            },
          },
        },
        Logo: {
          x: LOGO_X_SHOWCASE,
          y: LOGO_Y_SHOWCASE,
        },
      },
    };
  }

  override _onDataProvided() {
    const { show, recommended, listItemContext, isMyStuffAdded } =
      this.pageData;

    const { featuredTrailer } = show;
    this._movie = show;
    this._featuredTrailer = featuredTrailer?.id ? featuredTrailer : null;

    if (listItemContext) {
      this._listItemContext = listItemContext;
    }

    this._ShowcaseSection.patch({ show, isMyStuffAdded, pageId: this._pageId });
    this._RecommendedSection.recommended = recommended;
    this.updateLogo();
  }

  private updateFeaturedEpisode() {
    if (!this._movie) return;
    const { firstEpisode } = this._movie;
    this._featuredEpisode = firstEpisode;

    this._ShowcaseSection.patch({
      featuredEpisode: this._featuredEpisode,
      history: this.pageData.history,
    });
  }

  override _setup() {
    super._setup();
    this._setState('ShowcaseSection');
  }

  override _onChanged() {
    this.updateFeaturedEpisode();
    this._setState('ShowcaseSection');
  }

  override _handleUp() {
    if (this._getState() !== 'ShowcaseSection')
      this._setState('ShowcaseSection');
    else super._handleUp();
  }

  override _handleDown() {
    if (this.pageData.recommended.length) {
      this._setState('RecommendedSection');
    }
  }

  override _inactive() {
    this._setState('ShowcaseSection');
  }

  private updateLogo() {
    // Reset logo texture
    this._Logo.patch({ texture: undefined });

    const images = this._movie!.images;
    const logoSrc = images.image_logo ?? images.image_logo_white ?? '';
    const logoTexture = getImageTextureObj(
      logoSrc,
      LOGO_W_SHOWCASE,
      LOGO_H_SHOWCASE,
    );
    this._Logo.patch(logoTexture);
  }

  private handleScroll() {
    if (this._getState() === 'ShowcaseSection') {
      this._ShowcaseSection.setSmooth('y', 0);
      this._RecommendedSection.setSmooth('y', SHOWCASE_SECTION_HEIGHT);
      this._Logo.setSmooth('y', LOGO_Y_SHOWCASE);
    } else {
      this._ShowcaseSection.setSmooth('y', -SECTION_SCROLL_Y);
      this._RecommendedSection.setSmooth('y', RECOMMENDED_SECTION_FOCUSED_Y);
      this._Logo.setSmooth('y', LOGO_Y_RECOMMENDED);
    }
  }

  $onRecommendedHover() {
    if (this._getState() === 'ShowcaseSection') {
      this._setState('RecommendedSection');
    }
  }

  $onShowcaseHover() {
    if (this._getState() === 'RecommendedSection') {
      this._setState('ShowcaseSection');
    }
  }

  $playContent() {
    this._featuredEpisode &&
      navigateToPlay(
        this._featuredEpisode.guid,
        {
          viewContext: this.viewContext,
          listItemContext: this._listItemContext,
          pageId: this.pageId,
        },
        'show',
      );
  }

  $playTrailer() {
    this._featuredTrailer &&
      navigateToPlay(this._featuredTrailer.id, {
        viewContext: this.viewContext,
        listItemContext: this._listItemContext,
        pageId: this.pageId,
      });
  }

  $modalToggled(args: { visible: boolean }) {
    this.topNavEnable = !args.visible;
  }

  static override _states() {
    return [
      class ShowcaseSection extends this {
        override $enter() {
          this.handleScroll();
        }

        override _getFocused() {
          return this._ShowcaseSection;
        }
      },
      class RecommendedSection extends this {
        override $enter() {
          this.handleScroll();
        }

        override _getFocused() {
          return this._RecommendedSection;
        }

        override _handleBack() {
          this._setState('ShowcaseSection');
        }
      },
    ];
  }
}
